import { combineLatestWith, filter, map, tap } from 'rxjs';
import { toPromise } from '../../core/helpers/toPromise';
import { userOfferVotes } from '../../votes/endpoints';
import { getDealsByNickname as getDealsByNicknameEndpoint } from '../endpoints';
import { GetDealsByNicknameResult } from '../types/GetDealsByNickname';

export function getDealsByNicknameSSR(nickname: string) {
  return getDealsByNicknameEndpoint
    .requestAsPromise({ nickname })
    .then((result) => result.data)
    .catch(() => undefined);
}

export function populateDealsByNicknameFromSSR(
  nickname: string,
  result: GetDealsByNicknameResult
) {
  const offerIds = result?.edges?.map((edge) => edge.id);
  getDealsByNicknameEndpoint.cache$.next({
    currentNickname: nickname,
    loading: false,
    paginationLoading: false,
    pageInfo: result?.pageInfo,
    deals: result?.edges || [],
  });
  userOfferVotes.requestAsPromise({ offerIds });
}

export const getDealsByNickname = (nickname: string, after?: string) =>
  toPromise(
    getDealsByNicknameEndpoint.request({ nickname, offersAfter: after }).pipe(
      filter(({ data }) => !!data),
      tap((message) => {
        const offerIds = message.data?.edges.map((deal) => deal.id) || [];
        userOfferVotes.requestAsPromise({ offerIds });
      })
    )
  );

export const dealsCacheWithVote$ = getDealsByNicknameEndpoint.cache$.pipe(
  combineLatestWith(userOfferVotes.cache$),
  map(([dealsCache, votes]) => {
    const nextTemperature = (dealId: string, current?: number) =>
      typeof votes?.[dealId]?.temperature === 'number'
        ? votes?.[dealId]?.temperature
        : current;

    const mappedDeals = dealsCache?.deals?.map((deal) => ({
      ...deal,
      temperature: nextTemperature(deal.id, deal.temperature) || 0,
      voteInfo: votes?.[deal.id],
    }));

    return {
      ...dealsCache,
      deals: mappedDeals,
    };
  })
);
